import { PROJECT_PHASE_LABELS, workspaceSchema, type WorkspaceProject } from '@fluxus/shared';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeftRight, Building2 } from 'lucide-react';
import { useEffect, useId, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { NativeSelect } from '@/components/ui/select';
import { useAuth } from '@/lib/auth/session';

/** Empresa no caminho das telas do produto: /empresas/:organizationId/... */
const WORKSPACE_PATH = /^\/empresas\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(?:\/|$)/i;

export function workspaceOrgFromPath(pathname: string): string | null {
  return WORKSPACE_PATH.exec(pathname)?.[1] ?? null;
}

export const workspaceKey = (organizationId: string) => ['workspace', organizationId];

export function useWorkspace(organizationId: string) {
  const { client } = useAuth();
  return useQuery({
    queryKey: workspaceKey(organizationId),
    queryFn: ({ signal }) =>
      client.request(`/organizations/${organizationId}/workspace`, { schema: workspaceSchema, signal }),
    staleTime: 60_000,
  });
}

const projectKey = (organizationId: string) => `fluxus.projeto.${organizationId}`;

function readStoredProject(organizationId: string): string | null {
  try {
    return localStorage.getItem(projectKey(organizationId));
  } catch {
    return null;
  }
}

/**
 * Projeto BSC em que a pessoa está trabalhando na empresa: o pedido no endereço (?projeto=, ex.: link de uma
 * notificação), o escolhido por ela (lembrado neste aparelho) ou, sem escolha, o mais recente em andamento.
 * Projeto arquivado pode ser escolhido para consulta (só leitura).
 */
export function useCurrentProject(organizationId: string) {
  const workspace = useWorkspace(organizationId);
  const [params, setParams] = useSearchParams();
  const [chosen, setChosen] = useState<string | null>(() => readStoredProject(organizationId));
  const projects = workspace.data?.projects ?? [];
  // O projeto do endereço vale quando existe na empresa (senão, o lembrado) e passa a ser o lembrado.
  const requested = params.get('projeto');
  const fromLink = requested && projects.some((p) => p.id === requested) ? requested : null;
  useEffect(() => {
    if (!fromLink) return;
    setChosen(fromLink);
    try {
      localStorage.setItem(projectKey(organizationId), fromLink);
    } catch {
      // Sem armazenamento no navegador: vale só nesta tela.
    }
  }, [organizationId, fromLink]);
  const project: WorkspaceProject | null =
    projects.find((p) => p.id === (fromLink ?? chosen)) ??
    projects.find((p) => p.status === 'ACTIVE') ??
    projects[0] ??
    null;
  const select = (projectId: string) => {
    setChosen(projectId);
    // Escolha da pessoa vale mais que o projeto do link: ele sai do endereço (recarregar mantém a escolha).
    if (params.has('projeto')) {
      setParams(
        (prev) => {
          const out = new URLSearchParams(prev);
          out.delete('projeto');
          return out;
        },
        { replace: true },
      );
    }
    try {
      localStorage.setItem(projectKey(organizationId), projectId);
    } catch {
      // Sem armazenamento no navegador: a escolha vale só nesta tela.
    }
  };
  return { workspace, projects, project, select };
}

/** Seletor do projeto (só aparece quando a empresa tem mais de um projeto; arquivados ao fim, para consulta). */
export function ProjectPicker({
  projects,
  project,
  onSelect,
}: {
  projects: WorkspaceProject[];
  project: WorkspaceProject | null;
  onSelect: (id: string) => void;
}) {
  const id = useId();
  if (!project) return null;
  if (projects.length < 2) {
    return (
      <p className="text-sm text-muted-foreground">
        Projeto <span className="font-semibold text-foreground">{project.name}</span> ·{' '}
        {project.status === 'ACTIVE' ? PROJECT_PHASE_LABELS[project.phase] : 'Arquivado'} ·{' '}
        {project.horizonStart}–{project.horizonEnd}
      </p>
    );
  }
  return (
    <div className="flex items-center gap-2">
      <label htmlFor={id} className="text-sm text-muted-foreground">
        Projeto
      </label>
      <div className="w-64 max-w-full">
        <NativeSelect id={id} value={project.id} onChange={(e) => onSelect(e.target.value)}>
          {projects
            .filter((p) => p.status === 'ACTIVE')
            .map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} ({p.horizonStart}–{p.horizonEnd})
              </option>
            ))}
          {projects.some((p) => p.status !== 'ACTIVE') ? (
            <optgroup label="Arquivados (só consulta)">
              {projects
                .filter((p) => p.status !== 'ACTIVE')
                .map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} ({p.horizonStart}–{p.horizonEnd}) · arquivado
                  </option>
                ))}
            </optgroup>
          ) : null}
        </NativeSelect>
      </div>
    </div>
  );
}

/** Cabeçalho do menu quando a equipe Fluxus está dentro de uma empresa cliente. */
export function WorkspaceHeader({ organizationId }: { organizationId: string }) {
  const workspace = useWorkspace(organizationId);
  return (
    <div className="mx-2 mb-4 rounded-lg border border-border bg-background p-3 lg:mx-0">
      <div className="flex items-center gap-2">
        <Building2 className="size-4 shrink-0 text-brand-strong dark:text-brand" aria-hidden />
        <p className="sr-only lg:not-sr-only text-xs font-semibold text-muted-foreground">Empresa</p>
      </div>
      <p
        className="mt-1 hidden truncate text-sm font-bold lg:block"
        title={workspace.data?.organization.name}
      >
        {workspace.data?.organization.name ?? '…'}
      </p>
      <Link
        to="/empresas"
        className="mt-1.5 hidden items-center gap-1 text-xs font-semibold text-brand-strong underline-offset-4 hover:underline lg:inline-flex dark:text-brand"
      >
        <ArrowLeftRight className="size-3.5" aria-hidden />
        Trocar empresa
      </Link>
    </div>
  );
}
